import React, { useMemo } from 'react';
import { Box, Flex, Text, SimpleGrid } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Coins, CurrencyDollar, Wallet, Receipt } from 'phosphor-react';
import { formatCurrency } from '../../../../../utils/FormatFunctions';
import { num } from '../../format';
import { IPessoalResumoFolha } from '../../types';
import { UI } from '../../ui';

/**
 * Painel HUD da folha: do bruto (proventos) ao líquido + encargos patronais,
 * com a composição do CUSTO TOTAL da empresa quebrada em segmentos consistentes.
 *
 * Identidade: custo_total = proventos + INSS patronal + FGTS
 *           = liquido + INSS segurados + IRRF + outras deduções + INSS patronal + FGTS
 */

interface FolhaPanelProps {
  folha?: IPessoalResumoFolha;
  qtdColaboradores: number;
}

const KpiTile = ({
  icon,
  label,
  value,
  sub,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color: string;
}) => (
  <Box
    position="relative"
    overflow="hidden"
    borderRadius="16px"
    p={4}
    bg="rgba(255,255,255,0.72)"
    backdropFilter="blur(12px) saturate(160%)"
    border="1px solid rgba(255,255,255,0.85)"
    boxShadow="0 10px 28px -16px rgba(15,23,42,0.35)"
  >
    <Box
      position="absolute"
      top="-30px"
      insetEnd="-30px"
      w="100px"
      h="100px"
      borderRadius="full"
      bg={`radial-gradient(circle, ${color}26, transparent 68%)`}
      pointerEvents="none"
    />
    <Flex align="center" gap={3} position="relative">
      <Flex
        align="center"
        justify="center"
        w="40px"
        h="40px"
        borderRadius="12px"
        bgGradient={`linear(to-br, ${color}, ${color}B0)`}
        color="white"
        flexShrink={0}
        boxShadow={`0 8px 18px -6px ${color}80`}
      >
        {icon}
      </Flex>
      <Box minW={0}>
        <Text
          fontSize="10px"
          fontWeight={800}
          color={UI.faint}
          textTransform="uppercase"
          letterSpacing="0.08em"
        >
          {label}
        </Text>
        <Text fontSize="17px" fontWeight={800} color={UI.ink} lineHeight="1.1" noOfLines={1}>
          {value}
        </Text>
        {sub && (
          <Text fontSize="11px" color={UI.muted} noOfLines={1}>
            {sub}
          </Text>
        )}
      </Box>
    </Flex>
  </Box>
);

const Glass = ({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
}) => (
  <Box
    borderRadius="18px"
    p={5}
    bg="rgba(255,255,255,0.7)"
    backdropFilter="blur(14px) saturate(160%)"
    border="1px solid rgba(255,255,255,0.85)"
    boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
  >
    <Flex justify="space-between" align="baseline" mb={4} gap={2}>
      <Text fontSize="13px" fontWeight={800} color={UI.ink}>
        {title}
      </Text>
      {sub && (
        <Text fontSize="11px" color={UI.faint} fontWeight={600}>
          {sub}
        </Text>
      )}
    </Flex>
    {children}
  </Box>
);

export function FolhaPanel({ folha, qtdColaboradores }: FolhaPanelProps) {
  const m = useMemo(() => {
    const proventos = num(folha?.proventos);
    const inssSeg = num(folha?.inss_segurados);
    const irrf = num(folha?.irrf);
    const inssPat = num(folha?.inss_patronal);
    const fgts = num(folha?.fgts);
    const liquido = num(folha?.liquido);
    const outrasDed = Math.max(proventos - liquido - inssSeg - irrf, 0);
    const custoTotal = proventos + inssPat + fgts;
    const encargos = inssPat + fgts;
    return {
      proventos,
      inssSeg,
      irrf,
      inssPat,
      fgts,
      liquido,
      outrasDed,
      custoTotal,
      encargos,
      encargoPct: proventos ? (encargos / proventos) * 100 : 0,
      custoMedio: qtdColaboradores ? custoTotal / qtdColaboradores : 0,
    };
  }, [folha, qtdColaboradores]);

  if (!folha || m.proventos === 0) {
    return (
      <Box textAlign="center" py={16} color={UI.muted}>
        Sem dados de folha no período.
      </Box>
    );
  }

  const segs = [
    { label: 'Líquido a pagar', value: m.liquido, color: '#16A34A' },
    { label: 'INSS segurados', value: m.inssSeg, color: '#3B82F6' },
    { label: 'IRRF', value: m.irrf, color: '#8B5CF6' },
    { label: 'Outras deduções', value: m.outrasDed, color: '#94A3B8' },
    { label: 'INSS patronal', value: m.inssPat, color: '#EA580C' },
    { label: 'FGTS', value: m.fgts, color: '#0D9488' },
  ].filter(s => s.value > 0);

  const encargoRows = [
    { label: 'INSS patronal', value: m.inssPat, color: '#EA580C' },
    { label: 'FGTS', value: m.fgts, color: '#0D9488' },
  ];
  const maxEnc = Math.max(m.inssPat, m.fgts, 1);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4} mb={5}>
        <KpiTile
          icon={<Coins size={20} weight="fill" />}
          label="Custo total"
          value={formatCurrency(m.custoTotal)}
          sub="proventos + encargos"
          color={UI.brand}
        />
        <KpiTile
          icon={<CurrencyDollar size={20} weight="fill" />}
          label="Proventos (bruto)"
          value={formatCurrency(m.proventos)}
          sub={`${qtdColaboradores} colaboradores`}
          color={UI.pos}
        />
        <KpiTile
          icon={<Wallet size={20} weight="fill" />}
          label="Líquido a pagar"
          value={formatCurrency(m.liquido)}
          sub="após deduções"
          color="#8B5CF6"
        />
        <KpiTile
          icon={<Receipt size={20} weight="fill" />}
          label="Custo médio / colab."
          value={formatCurrency(m.custoMedio)}
          sub={`encargos ${m.encargoPct.toFixed(1)}%`}
          color={UI.warn}
        />
      </SimpleGrid>

      <Glass title="Composição do custo" sub={`total ${formatCurrency(m.custoTotal)}`}>
        {/* barra empilhada */}
        <Flex
          h="22px"
          borderRadius="full"
          overflow="hidden"
          mb={4}
          boxShadow="inset 0 1px 3px rgba(15,23,42,0.12)"
        >
          {segs.map(s => (
            <Box
              key={s.label}
              w={`${(s.value / m.custoTotal) * 100}%`}
              bg={s.color}
              title={`${s.label}: ${formatCurrency(s.value)}`}
            />
          ))}
        </Flex>
        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
          {segs.map(s => (
            <Flex key={s.label} align="center" gap={2}>
              <Box w="10px" h="10px" borderRadius="full" bg={s.color} flexShrink={0} />
              <Box minW={0}>
                <Text fontSize="11px" color={UI.muted} fontWeight={600} noOfLines={1}>
                  {s.label}
                </Text>
                <Text fontSize="13px" fontWeight={800} color={UI.ink}>
                  {formatCurrency(s.value)}{' '}
                  <Text as="span" fontSize="10.5px" color={UI.faint} fontWeight={600}>
                    {((s.value / m.custoTotal) * 100).toFixed(1)}%
                  </Text>
                </Text>
              </Box>
            </Flex>
          ))}
        </SimpleGrid>
      </Glass>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mt={5}>
        <Glass title="Encargos sobre a folha" sub={`${m.encargoPct.toFixed(1)}% dos proventos`}>
          {encargoRows.map(r => (
            <Flex key={r.label} align="center" gap={3} mb={3}>
              <Text w="110px" fontSize="12px" fontWeight={700} color={UI.ink} flexShrink={0}>
                {r.label}
              </Text>
              <Box flex={1} h="10px" borderRadius="full" bg="rgba(15,23,42,0.06)" overflow="hidden">
                <Box h="100%" borderRadius="full" bg={r.color} w={`${(r.value / maxEnc) * 100}%`} />
              </Box>
              <Text
                w="96px"
                textAlign="right"
                fontSize="12.5px"
                fontWeight={800}
                color={UI.ink}
                flexShrink={0}
              >
                {formatCurrency(r.value)}
              </Text>
            </Flex>
          ))}
          <Text fontSize="11.5px" color={UI.muted} mt={2}>
            Encargos patronais (INSS + FGTS) somam {formatCurrency(m.encargos)} além dos proventos.
          </Text>
        </Glass>

        <Glass title="Do bruto ao líquido" sub="por colaborador (média)">
          {[
            { label: 'Proventos', value: m.proventos, color: UI.pos },
            { label: '− INSS segurados', value: -m.inssSeg, color: '#3B82F6' },
            { label: '− IRRF', value: -m.irrf, color: '#8B5CF6' },
            ...(m.outrasDed > 0
              ? [{ label: '− Outras deduções', value: -m.outrasDed, color: '#94A3B8' }]
              : []),
            { label: 'Líquido a pagar', value: m.liquido, color: UI.ink, total: true },
          ].map(r => (
            <Flex
              key={r.label}
              justify="space-between"
              align="center"
              py={2}
              borderTop={'total' in r ? '1px solid rgba(15,23,42,0.1)' : undefined}
              mt={'total' in r ? 1 : 0}
            >
              <Text
                fontSize="12.5px"
                fontWeight={'total' in r ? 800 : 600}
                color={'total' in r ? UI.ink : UI.muted}
              >
                {r.label}
              </Text>
              <Text
                fontSize="13px"
                fontWeight={'total' in r ? 800 : 700}
                color={r.value < 0 ? UI.neg : r.color}
              >
                {formatCurrency(r.value)}
              </Text>
            </Flex>
          ))}
        </Glass>
      </SimpleGrid>
    </motion.div>
  );
}
